import React, { useState, useEffect, useMemo } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import "./SnackTimeTable.css";

const daysOfWeek = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

// Crea el modelo vacío con titulo + franjas
const emptyModel = () =>
  daysOfWeek.reduce((acc, d) => {
    acc[d] = { titulo: "", franjas: [] };
    return acc;
  }, {});

const minutesDiff = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = String(start).split(":").map(Number);
  const [eh, em] = String(end).split(":").map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : 0;
};

// Migra formatos antiguos {start,end} -> {titulo:"", franjas:[{inicio,fin}]}
const migrateOldFormatIfNeeded = (parsed) => {
  const out = emptyModel();
  daysOfWeek.forEach((d) => {
    const v = parsed?.[d];
    if (!v) {
      out[d] = { titulo: "", franjas: [] };
      return;
    }
    // Nuevo formato
    if (typeof v === "object" && Array.isArray(v.franjas)) {
      const titulo = typeof v.titulo === "string" ? v.titulo : "";
      const franjas = v.franjas
        .filter(
          (f) => f && typeof f.inicio === "string" && typeof f.fin === "string"
        )
        .map((f) => ({ inicio: f.inicio, fin: f.fin }));
      out[d] = { titulo, franjas };
      return;
    }
    // Formato antiguo
    if (typeof v === "object" && (v.start || v.end)) {
      const inicio = v.start || "";
      const fin = v.end || "";
      const franjas = inicio || fin ? [{ inicio, fin }] : [];
      out[d] = { titulo: "", franjas };
      return;
    }
    // Cualquier otro caso
    out[d] = { titulo: "", franjas: [] };
  });
  return out;
};

const SnackTimeTable = ({ value = "", onChange }) => {
  const [schedule, setSchedule] = useState(emptyModel());

  // Cargar valor inicial y migrar si viene en formato viejo
  useEffect(() => {
    try {
      if (value) {
        const parsed = JSON.parse(value);
        const normalized = migrateOldFormatIfNeeded(parsed);
        setSchedule(normalized);
      } else {
        setSchedule(emptyModel());
      }
    } catch (error) {
      console.error("Error parsing timetable JSON:", error);
      setSchedule(emptyModel());
    }
  }, [value]);

  // Emitir siempre JSON string limpio
  const emit = (next) => {
    try {
      onChange && onChange(JSON.stringify(next));
    } catch {
      onChange && onChange("{}");
    }
  };

  const handleTitleChange = (day, titulo) => {
    setSchedule((prev) => {
      const next = { ...prev, [day]: { ...prev[day], titulo } };
      emit(next);
      return next;
    });
  };

  const addFranja = (day) => {
    setSchedule((prev) => {
      const franjas = [...prev[day].franjas, { inicio: "", fin: "" }];
      const next = { ...prev, [day]: { ...prev[day], franjas } };
      emit(next);
      return next;
    });
  };

  const updateFranja = (day, idx, field, timeValue) => {
    setSchedule((prev) => {
      const franjas = prev[day].franjas.map((f, i) =>
        i === idx ? { ...f, [field]: timeValue } : f
      );
      const next = { ...prev, [day]: { ...prev[day], franjas } };
      emit(next);
      return next;
    });
  };

  const removeFranja = (day, idx) => {
    setSchedule((prev) => {
      const franjas = prev[day].franjas.filter((_, i) => i !== idx);
      const next = { ...prev, [day]: { ...prev[day], franjas } };
      emit(next);
      return next;
    });
  };

  const totals = useMemo(() => {
    const t = {};
    daysOfWeek.forEach((d) => {
      t[d] = (schedule[d]?.franjas || []).reduce(
        (acc, f) => acc + minutesDiff(f.inicio, f.fin),
        0
      );
    });
    return t;
  }, [schedule]);

  return (
    <div className="horizontal-timetable">
      {daysOfWeek.map((day) => (
        <div key={day} className="timetable-day-card">
          <div className="day-title">{day}</div>

          {/* Título del día */}
          <Form.Group controlId={`${day}-titulo`} className="mb-2">
            <Form.Label className="mb-1">Título del día</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ej: Piernas y core"
              value={schedule[day]?.titulo || ""}
              onChange={(e) => handleTitleChange(day, e.target.value)}
            />
          </Form.Group>

          {/* Acciones */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">Franjas horarias</div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => addFranja(day)}
            >
              + Agregar franja
            </Button>
          </div>

          {/* Lista de franjas */}
          {(schedule[day]?.franjas || []).length === 0 && (
            <div className="text-muted small mb-2">
              No hay franjas. Usa “Agregar franja”.
            </div>
          )}

          {(schedule[day]?.franjas || []).map((f, idx) => (
            <Row
              key={`${day}-franja-${idx}`}
              className="g-2 align-items-end mb-2"
            >
              <Col xs={6}>
                <Form.Label className="small mb-1">Inicio</Form.Label>
                <Form.Control
                  type="time"
                  value={f.inicio || ""}
                  onChange={(e) =>
                    updateFranja(day, idx, "inicio", e.target.value)
                  }
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="small mb-1">Fin</Form.Label>
                <Form.Control
                  type="time"
                  value={f.fin || ""}
                  onChange={(e) =>
                    updateFranja(day, idx, "fin", e.target.value)
                  }
                />
              </Col>

              <Col xs={12} className="d-flex justify-content-between">
                <small className="text-muted">
                  {minutesDiff(f.inicio, f.fin)
                    ? `${minutesDiff(f.inicio, f.fin)} min`
                    : "-"}
                </small>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => removeFranja(day, idx)}
                >
                  Eliminar
                </Button>
              </Col>
            </Row>
          ))}

          {/* Total del día */}
          <div className="duration-info">
            {totals[day] ? (
              <small>Total día: {totals[day]} min</small>
            ) : (
              <small>-</small>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SnackTimeTable;
